/**
 * The native shell, seen from the web app.
 *
 * The same bundle runs in three places — an iPhone's Safari, an Android browser, and inside the
 * Android app — and only the last of those can answer the two questions that matter: was this
 * position invented, and will Android vouch for this phone. Everything here is optional by
 * construction: if the plugin is not there, every call returns "no" and the app behaves exactly
 * as the PWA always has.
 *
 * Nothing here is trusted on its own. A client saying "not a mock location" proves nothing; what
 * gives it weight is the integrity verdict alongside it, which the server checks with Google.
 */

type NativeLocation = {
  lat: number;
  lng: number;
  accuracy_m: number;
  time: number;
  /** Android's own answer: this fix came from a mock provider. */
  mock: boolean;
};

type NativeAppInfo = {
  package: string;
  version_name: string;
  version_code: number;
  /**
   * Who installed this copy: 'com.android.vending' for Play, the package installer for a
   * sideload, null when Android will not say. Decides where an out-of-date worker is sent.
   */
  install_source?: string | null;
  integrity_ready: boolean;
  integrity_unavailable?: string;
  /**
   * What happened when the app asked Android to let this WebView use passkeys. Anything not
   * beginning "enabled" means fingerprint sign-in cannot work here, however healthy it looks.
   */
  webauthn_status?: string;
};

/**
 * Whether this copy of the app can ask Android for a passkey itself.
 *
 * False in any browser, and false inside an app built before 0.2.04 — which is the app that falls
 * back to the WebView route, the one that never worked on two or three of these phones. A screen
 * can say that out loud instead of letting somebody tap eleven times.
 */
export const hasNativePasskey = (): boolean => !!plugin()?.getPasskey;

/** What asking Android for the passkey did. */
export type NativePasskey =
  /** Android answered: the assertion, in the shape the browser would have produced. */
  | { kind: 'ok'; response: unknown }
  /** No app, or an app too old to have the bridge. The caller falls back to the WebView. */
  | { kind: 'unavailable' }
  /** The bridge is there and the attempt failed. A reason, not a shrug. */
  | { kind: 'failed'; message: string };

/**
 * Sign in by asking Android for the passkey, rather than the WebView.
 *
 * This is the path that does not care which version of Android System WebView a phone has, which
 * is what stopped two or three employees signing in at all.
 *
 * A failure used to come back as null, indistinguishable from "this app has no bridge", and the
 * caller quietly tried the WebView instead. When that failed too, the only thing left on screen
 * was a cancelled-prompt hint, for a prompt nobody had cancelled. The reason is carried out now.
 */
export async function nativePasskeyAssertion(requestJson: string): Promise<NativePasskey> {
  const p = plugin();
  if (!p?.getPasskey) return { kind: 'unavailable' };
  try {
    const { responseJson } = await p.getPasskey({ requestJson });
    return { kind: 'ok', response: JSON.parse(responseJson) };
  } catch (err) {
    const e = err as { message?: string };
    return { kind: 'failed', message: e?.message || 'Android did not answer' };
  }
}

/** True when the app has been granted app-scoped WebAuthn. Unknown outside the app. */
export function webAuthnUsable(info: NativeAppInfo | null): boolean {
  return !info?.webauthn_status || info.webauthn_status.startsWith('enabled');
}

type ShiftReminderRequest = {
  /**
   * Milliseconds from now until the hours are complete, by the server's clock. A delay rather
   * than an instant: the phone's own date can be anything a worker sets it to.
   */
  in_ms: number;
  work_date: string;
  /** Where the phone asks whether to show it, with the WebView's own cookie. */
  api_base: string;
  title: string;
  body: string;
};

type SivaNative = {
  getLocation(): Promise<NativeLocation>;
  getIntegrityToken(o: { nonce: string }): Promise<{ token: string | null; unavailable?: string }>;
  getAppInfo(): Promise<NativeAppInfo>;
  /** A passkey assertion from Android's Credential Manager, bypassing the WebView. */
  getPasskey?: (o: { requestJson: string }) => Promise<{ responseJson: string }>;
  scheduleShiftReminder(o: ShiftReminderRequest): Promise<void>;
  cancelShiftReminder(): Promise<void>;
  saveFile(o: { name: string; mime: string; base64: string }): Promise<{ location: string }>;
};

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: { SivaNative?: SivaNative };
};

const cap = (): CapacitorGlobal | undefined =>
  (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;

const plugin = (): SivaNative | undefined => cap()?.Plugins?.SivaNative;

/** Running inside our Android app, rather than in a browser or an iPhone home-screen app. */
export function isNativeApp(): boolean {
  return !!plugin() && cap()?.getPlatform?.() === 'android';
}

let cachedInfo: NativeAppInfo | null = null;

/** What this copy of the app is. Null in any browser. */
export async function appInfo(): Promise<NativeAppInfo | null> {
  const p = plugin();
  if (!p) return null;
  if (cachedInfo) return cachedInfo;
  try {
    cachedInfo = await p.getAppInfo();
    return cachedInfo;
  } catch {
    return null;
  }
}

/**
 * A fresh position from the platform, with the mock flag. Null in a browser, and null if the
 * native read fails — the caller falls back to the browser position, because refusing to punch
 * because one API was unavailable would be worse than punching with a fix we trust slightly less.
 */
export async function nativeLocation(): Promise<NativeLocation | null> {
  const p = plugin();
  if (!p) return null;
  try {
    return await p.getLocation();
  } catch {
    return null;
  }
}

/**
 * A Play Integrity verdict bound to this punch.
 *
 * The nonce is the server's own punch challenge id, so the token cannot be lifted from one punch
 * and replayed onto another. Returning null is normal and not an error: a phone without Play
 * services, a build with no cloud project number yet, or Google being briefly unreachable all end
 * up here, and the server decides what a punch with no verdict is worth.
 */
export async function integrityToken(nonce: string): Promise<string | null> {
  const p = plugin();
  if (!p) return null;
  try {
    const r = await p.getIntegrityToken({ nonce });
    return r.token ?? null;
  } catch {
    return null;
  }
}

/**
 * Hand the phone an alarm for the moment the hours are complete. The phone owns it from here:
 * it survives the app being closed and the phone rebooting, and when it goes off the phone asks
 * the server whether the worker is still punched in before showing anything. Rejects when the
 * plugin is missing or the phone refused, and the caller treats that as "not this time".
 */
export async function nativeScheduleShiftReminder(o: ShiftReminderRequest): Promise<void> {
  const p = plugin();
  if (!p) throw new Error('not the native app');
  await p.scheduleShiftReminder(o);
}

/** Take the alarm back: the worker punched out, or nothing is open any more. */
export async function nativeCancelShiftReminder(): Promise<void> {
  const p = plugin();
  if (!p) throw new Error('not the native app');
  await p.cancelShiftReminder();
}

/**
 * Hand the phone a file to save to Downloads: a slip, an agreement, a report. A WebView has no
 * downloads — a download link does nothing in it, silently — so the page fetches the bytes with
 * its own session and the phone writes them to Downloads/SIVA and posts a "Downloaded" notice
 * that opens the file. Resolves with where it went; rejects outside the app.
 */
export async function nativeSaveFile(name: string, mime: string, blob: Blob): Promise<string> {
  const p = plugin();
  if (!p) throw new Error('not the native app');
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  const r = await p.saveFile({ name, mime, base64: btoa(binary) });
  return r.location;
}
